import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Layout,
  Dropdown,
  Button,
  Flex,
  Typography,
  Spin,
  Empty,
  message,
} from "antd";
import {
  MoreOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

import { formatDateTime, getUserType } from "../../utils/appUtils";
import { STATS_DATA } from "../../utils/constants";
import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import ImageCardDisplay from "../../components/common/ImageCardDisplay";
import {
  fetchMyEvents,
  selectMyEvents,
  selectFetchAllEventsStatus,
  selectFetchAllEventsError,
  fetchAllStats
} from "../../redux/eventSlice";
import {
  showLocationModal,
  selectFormattedLocation,
} from "../../redux/locationSlice";

const { Content } = Layout;
const { Title } = Typography;

/**
 * EVENT MAPPER - Transforms API response to UI format
 * Maps database field names to user-friendly field names
 * Formats dates to readable format: "DD MMM YYYY | hh:mm A"
 *
 * @param {Object} apiEvent - Raw event object from API
 * @returns {Object} Formatted event object for UI
 */
const mapApiEventToUIEvent = (apiEvent) => {
  // Helper to safely extract name from object or return string as-is
  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.name || value.event_type_name || value.venue_name || fallback;
    return fallback;
  };

  return {
    id: apiEvent.id,
    title: apiEvent.event_name || "Untitled Event",
    eventType: getStringValue(apiEvent.event_type, "Event"),
    location: getStringValue(apiEvent.event_venue, ""),
    address: getStringValue(apiEvent.event_venue, ""),
    startDate: formatDateTime(apiEvent.event_start_date_time),
    endDate: formatDateTime(apiEvent.event_end_date_time),
    media: apiEvent.media,
  };
};

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeRole = getUserType();

  // Redux selectors
  const rawEventsData = useSelector(selectMyEvents);
  const loading = useSelector(selectFetchAllEventsStatus);
  const error = useSelector(selectFetchAllEventsError);
  const formattedLocation = useSelector(selectFormattedLocation);
  const allStats = useSelector((state) => state.event.allStats);
  const statsLoading = useSelector((state) => state.event.fetchAllStatsLoading);
  
  // Handler to open location modal
  const handleChangeLocation = () => {
    dispatch(showLocationModal());
  };

  // Fetch events on component mount
  useEffect(() => {
    dispatch(fetchMyEvents());
    dispatch(fetchAllStats());
  }, [dispatch]);


  // Show error message if fetch fails
  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  // Transform API events to UI format
  const eventsData = useMemo(() => {
    if (!rawEventsData?.data || !Array.isArray(rawEventsData.data)) {
      return [];
    }
    return rawEventsData.data.map(mapApiEventToUIEvent);
  }, [rawEventsData]);

const getMenuItems = (eventId) => {
  const items = [
    {
      key: "view",
      icon: <FileTextOutlined />,
      label: "View Details",
      onClick: () => {
        const rawEvent = rawEventsData?.data?.find(e => e.id === eventId);

        if (rawEvent?.slug) {
          navigate(`/event-details/${rawEvent.slug}`, {
            state: { event: rawEvent }
          });
        }
      },
    },
  ];

  if (activeRole === "organizer") {
    items.push(
      {
        key: "edit",
        icon: <EditOutlined />,
        label: "Edit",
        onClick: () => navigate(`/edit-event/${eventId}`),
      },
      {
        key: "duplicate",
        icon: <CopyOutlined />,
        label: "Duplicate",
        onClick: () => console.log("Duplicate", eventId),
      },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Delete",
        onClick: () => console.log("Delete", eventId),
      }
    );
  }

  return items;
};

  return (
    <PageLayout title="Dashboard">
      {/* SEO HELMET WRAPPER */}
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
          and city-specific visibility for organizers and users." 
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />
      <Content>
        {/* Location Bar - Click to change location */}
        <section className="locationBar" style={{
          marginBottom: 20,
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb',
        }}>
          <Flex align="center" gap={8}>
            <span className="material-symbols-outlined" style={{ color: '#2563eb', fontSize: 20 }}>
              location_on
            </span>
            <span style={{ color: '#111827', fontWeight: 500 }}>
              Your Location:
            </span>
            <span style={{ color: '#2563eb', fontWeight: 600 }}>
              {formattedLocation}
            </span>
          </Flex>
          <Button
            type="link"
            onClick={handleChangeLocation}
            style={{ padding: 0, height: 'auto', color: '#2563eb' }}
          >
            <Flex align="center" gap={4}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                edit_location_alt
              </span>
              Change
            </Flex>
          </Button>
        </section>

        {/* STATS SECTION */}
        <section className="statSec">
          <Row gutter={[20, 20]}>
            <Spin spinning={statsLoading} tip="Loading stats..." />
            {allStats && Object.keys(allStats).length > 0 && STATS_DATA.map((stat, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card hoverable className="statCard">
                  <div className="statCardContent">
                    <div className={`statIcon ${stat.colorClass}`}>
                      {stat.icon}
                    </div>
                    <div className="stat-info">
                      <div className="statLabel">{stat.title}</div>
                      <div className="statValue">{allStats[stat.id] ||  0}</div>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* EVENTS SECTION */}
        <section className="eventsSec">
          <Row gutter={[20, 20]}>
            <Col xs={24}>
              <Title level={2} style={{ marginBottom: 0 }}>
                Current Event
              </Title>
            </Col>
            <Col xs={24}>
              <Spin spinning={loading === "loading"} tip="Loading events...">
                {eventsData && eventsData.length > 0 ? (
                  <Row gutter={[20, 20]}>
                    {eventsData.map((event) => (
                      <Col xs={24} sm={24} md={12} lg={12} key={event.id}>
                        <Card hoverable className="eventCard">
                          <Dropdown
                            menu={{ items: getMenuItems(event.id) }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <Button
                              icon={<MoreOutlined />}
                              size="small"
                              type="text"
                              className="eventActionMenu noStyleBtn"
                            />
                          </Dropdown>

                          <div className="eventHeader">
                            <div className="eventImgBlk">
                              <ImageCardDisplay cardElement={event} isCard={true} />
                              <Button className="eventBtnOnImg yellowBtn">
                                {event.eventType}
                              </Button>
                            </div>
                            <Flex
                              vertical
                              gap={8}
                              className="eventInfo"
                              justify="center"
                            >
                              <Flex vertical gap={8}>
                                <h3 className="eventTitle">{event.title}</h3>
                                <Flex vertical>
                                  <p className="eventLocation">
                                    {event.location}
                                  </p>
                                  <p className="eventAddress">
                                    {event.address}
                                  </p>
                                </Flex>
                                <div className="eventDates">
                                  <div className="dateItem">
                                    <span className="dateLabel">Start</span>
                                    <span className="dateValue">
                                      {event.startDate}
                                    </span>
                                  </div>
                                  <div className="dateItem">
                                    <span className="dateLabel">End</span>
                                    <span className="dateValue">
                                      {event.endDate}
                                    </span>
                                  </div>
                                </div>
                              </Flex>
                            </Flex>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  !loading && (
                    <Empty description="No events found" />
                  )
                )}
              </Spin>
            </Col>
          </Row>
        </section>
      </Content>

    </PageLayout>
  );
}
